// src/portfolio/DocView.jsx — renders Doc blocks (markdown-ish) to JSX
import React from 'react';
import { FILE_BY_ID } from './data';
import { FileGlyph, MONO } from './icons';
import Resume from './Resume';

// Inline code-block tokens (paired): %k% %s% %n% %f% %t% %c% %p%  close with %/%
function renderCode(src, sx) {
  const out = [];
  const re = /%([ksnftcp])%([\s\S]*?)%\/%/g;
  let last = 0;
  let i = 0;
  const color = (tag) => ({ k: sx.key, s: sx.str, n: sx.num, f: sx.fn, t: sx.type, c: sx.cmt, p: sx.punct }[tag]);
  let m;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) out.push(<span key={i++}>{src.slice(last, m.index)}</span>);
    const [, tag, inner] = m;
    out.push(
      <span key={i++} style={{ color: color(tag), fontStyle: tag === 'c' ? 'italic' : undefined }}>{inner}</span>
    );
    last = m.index + m[0].length;
  }
  if (last < src.length) out.push(<span key={i++}>{src.slice(last)}</span>);
  return out;
}

// Inline markdown for prose: **bold**, *italic*, `code`, [label](#id)
function renderInline(src, theme, onLink) {
  if (!src) return null;
  const parts = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\(#[^)]+\))/g;
  let last = 0; let i = 0; let m;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) parts.push(<React.Fragment key={i++}>{src.slice(last, m.index)}</React.Fragment>);
    const s = m[0];
    if (s.startsWith('**')) {
      parts.push(<strong key={i++} style={{ color: theme.fgStrong, fontWeight: 600 }}>{s.slice(2, -2)}</strong>);
    } else if (s.startsWith('*')) {
      parts.push(<em key={i++} style={{ color: theme.mute, fontStyle: 'italic' }}>{s.slice(1, -1)}</em>);
    } else if (s.startsWith('`')) {
      parts.push(
        <code key={i++} style={{
          fontFamily: MONO, fontSize: '0.92em',
          background: theme.surface, padding: '1px 6px', borderRadius: 4,
          color: theme.sx.str, border: `1px solid ${theme.border}`,
        }}>{s.slice(1, -1)}</code>
      );
    } else {
      const labelEnd = s.indexOf(']');
      const label = s.slice(1, labelEnd);
      const id = s.slice(labelEnd + 3, -1);
      parts.push(
        <button key={i++} type="button" onClick={() => onLink && onLink(id)} style={{
          color: theme.sx.link, textDecoration: 'underline', textUnderlineOffset: 3,
          textDecorationColor: theme.borderHi, padding: 0, cursor: 'pointer',
          background: 'none', border: 0, font: 'inherit',
        }}>{label}</button>
      );
    }
    last = m.index + s.length;
  }
  if (last < src.length) parts.push(<React.Fragment key={i++}>{src.slice(last)}</React.Fragment>);
  return parts;
}

function applyPlaceholders(s, ident) {
  if (!s) return s;
  return s.replace(/\{(\w+)\}/g, (_, k) => ident[k] ?? `{${k}}`);
}

// Hero block — colored banner that opens a section. Bleeds out of the doc
// padding so it reads as a section header, not a paragraph.
function HeroBlock({ block, theme, apply }) {
  const accent = block.accent || theme.accent;
  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      margin: '-36px -56px 28px',
      padding: '44px 56px 36px',
      background: `linear-gradient(135deg, ${accent}22 0%, ${theme.surface} 60%, ${theme.bg} 100%)`,
      borderBottom: `1px solid ${theme.border}`,
    }}>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.5,
        background: `linear-gradient(${theme.border}40 1px, transparent 1px) 0 0 / 100% 24px`,
      }} />
      <div style={{ position: 'relative' }}>
        {block.eyebrow && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '4px 10px', borderRadius: 999,
            border: `1px solid ${accent}55`, background: `${accent}15`,
            fontFamily: MONO, fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase',
            color: accent, marginBottom: 18,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent }} />
            {apply(block.eyebrow)}
          </div>
        )}
        <h1 style={{
          fontSize: 46, lineHeight: 1.05, letterSpacing: '-0.025em', fontWeight: 600,
          margin: 0, color: theme.fgStrong, maxWidth: 620,
        }}>
          {apply(block.title)}
        </h1>
        {block.tagline && (
          <p style={{
            margin: '14px 0 0', fontSize: 17, lineHeight: 1.5,
            color: theme.mute, maxWidth: 560,
          }}>
            {apply(block.tagline)}
          </p>
        )}
        {block.meta && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '6px 16px',
            marginTop: 22, fontFamily: MONO, fontSize: 11, color: theme.dim, letterSpacing: '.02em',
          }}>
            {block.meta.map((m, i) => (
              <span key={i}>
                {i > 0 && <span style={{ color: theme.dim, marginRight: 16 }}>·</span>}
                {apply(m)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Cards block — grid of clickable project tiles linked to file ids.
// Optional `cover` field renders a 16:9 strip at the top of the card.
//   cover: { kind: 'code',     lines: ['line 1', ...] }    // monospace, syntax-tokenised
//   cover: { kind: 'image',    src: '/url.png', alt: '' }   // real screenshot
//   cover: { kind: 'gradient', from: '#xxx', to: '#xxx' }   // abstract band
function CardsBlock({ block, theme, onOpen }) {
  const items = block.items || [];
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
      gap: 14, margin: '6px 0 28px',
    }}>
      {items.map((it, i) => (
        <button
          key={i}
          type="button"
          onClick={() => it.id && onOpen && onOpen(it.id)}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = theme.accent;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme.border;
            e.currentTarget.style.transform = 'none';
          }}
          style={{
            textAlign: 'left', cursor: 'pointer',
            background: theme.surface, border: `1px solid ${theme.border}`,
            borderRadius: 8, padding: 0, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            transition: 'border-color .15s, transform .15s',
            minHeight: 130, font: 'inherit', color: 'inherit',
          }}
        >
          {it.cover && <CardCover cover={it.cover} theme={theme} accent={it.accent || theme.accent} />}
          <div style={{
            padding: '12px 14px 14px',
            display: 'flex', flexDirection: 'column', gap: 6, flex: 1, position: 'relative',
          }}>
            {!it.cover && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                background: it.accent || theme.accent, opacity: 0.6,
              }} />
            )}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              fontFamily: MONO, fontSize: 10, color: theme.dim, letterSpacing: '.1em', textTransform: 'uppercase',
            }}>
              <span>{it.tag || 'project'}</span>
              {it.status && <span style={{ color: it.accent || theme.accent }}>● {it.status}</span>}
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: theme.fgStrong, lineHeight: 1.25, marginTop: 2 }}>
              {it.title}
            </div>
            {it.desc && (
              <div style={{ fontSize: 12.5, color: theme.mute, lineHeight: 1.45, flex: 1 }}>
                {it.desc}
              </div>
            )}
            {it.stack && (
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4,
                fontFamily: MONO, fontSize: 10, color: theme.dim,
              }}>
                {it.stack.map((s, j) => (
                  <span key={j} style={{
                    padding: '1px 6px', border: `1px solid ${theme.border}`, borderRadius: 3,
                  }}>{s}</span>
                ))}
              </div>
            )}
            <div style={{
              marginTop: 'auto', paddingTop: 6, fontFamily: MONO, fontSize: 11,
              color: theme.sx.link, display: 'flex', alignItems: 'center', gap: 4,
            }}>
              open <span>→</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// Card cover — three styles, always 16:9, top of card.
function CardCover({ cover, theme, accent }) {
  if (cover.kind === 'image' && cover.src) {
    return (
      <div style={{
        width: '100%', aspectRatio: '16 / 9',
        background: theme.surface2, borderBottom: `1px solid ${theme.border}`, overflow: 'hidden',
      }}>
        <img
          src={cover.src}
          alt={cover.alt || ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          loading="lazy"
        />
      </div>
    );
  }
  if (cover.kind === 'code') {
    const lines = cover.lines || [];
    return (
      <div style={{
        width: '100%', aspectRatio: '16 / 9',
        background: theme.bg, borderBottom: `1px solid ${theme.border}`,
        fontFamily: MONO, fontSize: 10, lineHeight: 1.55, color: theme.fg,
        padding: '10px 12px', overflow: 'hidden', position: 'relative',
      }}>
        <div style={{
          position: 'absolute', top: 8, right: 10, display: 'flex', gap: 4, opacity: 0.5,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: theme.dim }} />
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: theme.dim }} />
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: theme.dim }} />
        </div>
        {lines.slice(0, 7).map((line, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, whiteSpace: 'nowrap', overflow: 'hidden' }}>
            <span style={{ color: theme.dim, width: 14, textAlign: 'right', flex: '0 0 auto' }}>{i + 1}</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{renderCode(line, theme.sx)}</span>
          </div>
        ))}
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, height: 24,
          background: `linear-gradient(transparent, ${theme.bg})`, pointerEvents: 'none',
        }} />
      </div>
    );
  }
  if (cover.kind === 'gradient') {
    return (
      <div style={{
        width: '100%', aspectRatio: '16 / 9',
        background: `linear-gradient(135deg, ${cover.from || accent}, ${cover.to || theme.surface})`,
        borderBottom: `1px solid ${theme.border}`,
      }} />
    );
  }
  return null;
}

// YouTube embed (privacy-enhanced, 16:9, lazy-loaded). Accepts either a
// bare 11-character video ID or any full YouTube URL.
function YouTubeBlock({ id, caption, theme }) {
  const ytId = (() => {
    if (!id) return null;
    if (/^[\w-]{11}$/.test(id)) return id;
    const m = id.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
    return m ? m[1] : null;
  })();
  if (!ytId) return null;
  return (
    <div style={{ margin: '14px 0 28px', maxWidth: 660 }}>
      <div style={{
        position: 'relative', width: '100%', aspectRatio: '16 / 9',
        background: theme.surface, borderRadius: 8, overflow: 'hidden',
        border: `1px solid ${theme.border}`,
        boxShadow: '0 12px 36px rgba(0,0,0,0.45)',
      }}>
        <iframe
          title={caption || 'YouTube video'}
          src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1`}
          loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        />
      </div>
      {caption && (
        <div style={{ marginTop: 8, fontSize: 11, color: theme.dim, fontFamily: MONO, letterSpacing: '.02em' }}>
          ▶ {caption}
        </div>
      )}
    </div>
  );
}

export default function DocView({ doc, theme, identity, onOpen, isMobile }) {
  if (!doc) return null;
  const apply = (s) => applyPlaceholders(s, identity);

  const padX = isMobile ? 18 : 56;
  const padTop = isMobile ? 22 : 36;
  const padBot = isMobile ? 80 : 120;
  const h1Size = isMobile ? 28 : 38;
  const h1HashSize = isMobile ? 22 : 30;
  const subSize = isMobile ? 15 : 17;
  const bodySize = isMobile ? 14 : 14.5;

  return (
    <div className="doc-in" key={doc.title} style={{
      maxWidth: 760, margin: '0 auto',
      padding: `${padTop}px ${padX}px ${padBot}px`,
      color: theme.fg, fontFamily: "'Inter', sans-serif",
      fontSize: bodySize, lineHeight: 1.62,
    }}>
      {doc.blocks.map((b, i) => {
        switch (b.type) {
          case 'meta':
            return (
              <div key={i} style={{ display: 'flex', gap: 8, fontFamily: MONO, fontSize: 11, color: theme.dim, marginBottom: 18, letterSpacing: '.02em', flexWrap: 'wrap' }}>
                {b.items.map((it, j) => <span key={j}>{it}</span>)}
              </div>
            );
          case 'h1':
            return (
              <h1 key={i} style={{ fontSize: h1Size, lineHeight: 1.08, letterSpacing: '-0.02em', fontWeight: 600, margin: '0 0 8px', color: theme.fgStrong }}>
                <span style={{ color: theme.sx.head, fontFamily: MONO, fontSize: h1HashSize, marginRight: 12, fontWeight: 400, verticalAlign: '2px' }}>#</span>
                {apply(b.text)}
              </h1>
            );
          case 'subtitle':
            return <p key={i} style={{ fontSize: subSize, color: theme.mute, fontWeight: 400, margin: '0 0 28px', lineHeight: 1.45 }}>{apply(b.text)}</p>;
          case 'h2':
            return (
              <h2 key={i} style={{ fontSize: 18, fontWeight: 600, color: theme.fgStrong, margin: '40px 0 12px', display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ color: theme.sx.head, fontFamily: MONO, fontSize: 14, fontWeight: 400 }}>##</span>
                {apply(b.text)}
              </h2>
            );
          case 'h3':
            return <h3 key={i} style={{ fontSize: 15, fontWeight: 600, color: theme.fgStrong, margin: '24px 0 8px' }}>{apply(b.text)}</h3>;
          case 'p':
            return <p key={i} style={{ margin: '0 0 14px', maxWidth: 660 }}>{renderInline(apply(b.text), theme, onOpen)}</p>;
          case 'ul':
            return (
              <ul key={i} style={{ margin: '0 0 18px', paddingLeft: 0, listStyle: 'none', maxWidth: 660 }}>
                {b.items.map((t, j) => (
                  <li key={j} style={{ display: 'flex', gap: 12, padding: '5px 0' }}>
                    <span style={{ color: theme.sx.head, fontFamily: MONO, fontSize: 13, flex: '0 0 auto', marginTop: 2 }}>—</span>
                    <span>{renderInline(apply(t), theme, onOpen)}</span>
                  </li>
                ))}
              </ul>
            );
          case 'chips':
            return (
              <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '4px 0 18px' }}>
                {b.items.map((t) => (
                  <span key={t} style={{
                    fontSize: 12, padding: '4px 10px', border: `1px solid ${theme.border}`,
                    borderRadius: 999, background: theme.surface, fontFamily: MONO, color: theme.fg, letterSpacing: '.01em',
                  }}>{t}</span>
                ))}
              </div>
            );
          case 'kv':
            return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', columnGap: 18, rowGap: 6, margin: '6px 0 24px', fontSize: 13, fontFamily: MONO, maxWidth: 660 }}>
                {b.rows.map(([k, v], j) => (
                  <React.Fragment key={j}>
                    <div style={{ color: theme.dim }}>{k}</div>
                    <div style={{ color: theme.fg }}>{v}</div>
                  </React.Fragment>
                ))}
              </div>
            );
          case 'callout':
            return (
              <div key={i} style={{
                display: 'flex', gap: 12, padding: '12px 16px', borderRadius: 6,
                background: theme.surface, border: `1px solid ${theme.border}`,
                borderLeft: `3px solid ${b.kind === 'tip' ? theme.sx.link : theme.sx.num}`,
                margin: '14px 0 22px', maxWidth: 660, fontSize: 13.5, color: theme.fg,
              }}>
                <span style={{
                  fontFamily: MONO, fontSize: 11,
                  color: b.kind === 'tip' ? theme.sx.link : theme.sx.num,
                  textTransform: 'uppercase', letterSpacing: '.12em', marginTop: 3, flex: '0 0 auto',
                }}>{b.kind}</span>
                <span>{renderInline(apply(b.text), theme, onOpen)}</span>
              </div>
            );
          case 'fileref':
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2, margin: '6px 0 20px', maxWidth: 660 }}>
                {b.items.map((it) => {
                  const file = FILE_BY_ID[it.id];
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onOpen && onOpen(it.id)}
                      onMouseEnter={(e) => (e.currentTarget.style.background = theme.surface)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 10px', borderRadius: 5, textAlign: 'left',
                        background: 'transparent', transition: 'background .12s', cursor: 'pointer',
                        border: 0, color: 'inherit', font: 'inherit',
                      }}
                    >
                      <FileGlyph kind={file?.kind ?? 'md'} theme={theme} />
                      <span style={{ color: theme.sx.link, fontFamily: MONO, fontSize: 13 }}>{file?.path ?? it.label}</span>
                      <span style={{ color: theme.dim, fontSize: 13 }}>{it.hint}</span>
                      <span style={{ marginLeft: 'auto', color: theme.dim, fontSize: 13 }}>→</span>
                    </button>
                  );
                })}
              </div>
            );
          case 'code':
            return (
              <div key={i} style={{
                margin: '8px 0 22px', borderRadius: 6, border: `1px solid ${theme.border}`,
                background: theme.surface, fontFamily: MONO, fontSize: 12.5, color: theme.fg, overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '6px 12px', borderBottom: `1px solid ${theme.border}`, fontSize: 10.5, color: theme.dim,
                  letterSpacing: '.06em', textTransform: 'uppercase', background: theme.surface2,
                }}>
                  <span>{b.lang}</span>
                  <span style={{ color: theme.dim }}>copy</span>
                </div>
                <pre style={{ margin: 0, padding: '14px 16px', whiteSpace: 'pre', overflow: 'auto', lineHeight: 1.65 }}>
                  {renderCode(b.text, theme.sx)}
                </pre>
              </div>
            );
          case 'table':
            return (
              <div key={i} style={{ margin: '6px 0 20px', borderRadius: 6, border: `1px solid ${theme.border}`, overflow: 'hidden', fontFamily: MONO, fontSize: 12.5 }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: `repeat(${b.headers.length}, 1fr)`,
                  background: theme.surface2, color: theme.dim, padding: '6px 12px',
                  fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase',
                }}>
                  {b.headers.map((h) => <div key={h}>{h}</div>)}
                </div>
                {b.rows.map((r, j) => (
                  <div key={j} style={{
                    display: 'grid', gridTemplateColumns: `repeat(${b.headers.length}, 1fr)`,
                    padding: '8px 12px', borderTop: `1px solid ${theme.border}`, color: theme.fg,
                  }}>
                    {r.map((cell, k) => <div key={k} style={k === 0 ? { color: theme.sx.num } : undefined}>{cell}</div>)}
                  </div>
                ))}
              </div>
            );
          case 'youtube':
            return <YouTubeBlock key={i} id={b.id} caption={b.caption} theme={theme} />;
          case 'hero':
            return <HeroBlock key={i} block={b} theme={theme} apply={apply} />;
          case 'cards':
            return <CardsBlock key={i} block={b} theme={theme} onOpen={onOpen} />;
          case 'pdf':
            return <Resume key={i} />;
          default:
            return null;
        }
      })}

      {/* Trailing cursor — the small peak-end signature */}
      <div style={{ marginTop: 32, color: theme.dim, fontFamily: MONO, fontSize: 12 }}>
        <span style={{ color: theme.sx.link }}>$</span> <span className="caret" />
      </div>
    </div>
  );
}
